/**
 * Разделы портала и кто их видит — §2 и §5.1 ТЗ портала.
 *
 * Меню строится из `GET /api/platform/navigation`: раздела, на который
 * нет права, нет в меню вовсе (пустых разделов не показываем). Сервер всё
 * равно проверяет право на каждом запросе — меню лишь не зовёт туда, где
 * человеку откажут.
 *
 * Разделы следующих этапов видны с пометкой «скоро» и ссылкой на то
 * место, где это делается сейчас.
 */

import type { Navigation } from './api';
import { SECTION_READY } from './sections/registry';

export type IconName =
  | 'today'
  | 'receipts'
  | 'payments'
  | 'shift'
  | 'finance'
  | 'stock'
  | 'study'
  | 'team'
  | 'accountant'
  | 'settings';

export interface SectionDef {
  code: IconName;
  title: string;
  path: string;
  group: 'Работа' | 'Цифры' | 'Люди' | 'Система';
  /** Готов ли раздел на этом этапе. Нет — экран «скоро». */
  ready: boolean;
  /** Что внутри — для экрана «скоро». */
  about: string;
  /** Где это сейчас. */
  nowWhere?: { text: string; href?: string };
}

/**
 * Куда отправить человека, пока раздел не готов.
 *
 * Адресов ресторана здесь больше нет: до 20.09.2026 в этом файле стояли
 * `t.me/toscana_ops_bot` и `finance.toscanagrill.spb.ru`, то есть второй
 * ресторан получил бы ссылки на чужие боты. Имя заказчика — настройка, а
 * не строка в коде (D5: продукт передаётся и разворачивается заново).
 *
 * Ссылки нет — `Soon` покажет только текст, и это честно: «пока это
 * делается в операционном боте» человек понимает и без кнопки.
 */
const IN_OPS_BOT = { text: 'в операционном боте' };

export const SECTIONS: SectionDef[] = [
  {
    code: 'today',
    title: 'Сегодня',
    path: '/',
    group: 'Работа',
    ready: true,
    about: 'Ваши дела от всех агентов одной лентой.',
  },
  {
    code: 'receipts',
    title: 'Накладные',
    path: '/receipts',
    group: 'Работа',
    ready: true,
    about: 'Фото накладной рядом с черновиком StoreHouse: проверить и провести.',
  },
  {
    // «Оплаты» и «Сверка» — один раздел с 21.10.2026 (D267): платёжки
    // недели, карточка каждого поставщика с приходами и оплатами, выписка
    // банка. Вкладки открываются каждая своей галочкой (D235).
    code: 'payments',
    title: 'Оплаты',
    path: '/payments',
    group: 'Работа',
    ready: SECTION_READY.payments,
    about: 'Платёжки недели, карточка каждого поставщика — приходы и оплаты, выписка банка, долги и отчёты.',
    nowWhere: IN_OPS_BOT,
  },
  {
    code: 'shift',
    title: 'Смена',
    path: '/shift',
    group: 'Работа',
    ready: SECTION_READY.shift,
    about: 'Чек-листы дня по участкам, задачи, табель, шаблоны и дисциплина смены.',
    nowWhere: IN_OPS_BOT,
  },
  {
    code: 'finance',
    title: 'Финансы',
    path: '/finance',
    group: 'Цифры',
    ready: SECTION_READY.finance,
    about: 'Обзор, продажи, себестоимость, меню, чеки, контроль.',
    nowWhere: { text: 'в дашборде финансов' },
  },
  {
    code: 'stock',
    title: 'Склад',
    path: '/stock',
    group: 'Цифры',
    ready: SECTION_READY.stock,
    about: 'Аудит номенклатуры, блюда кассы и товары склада, цены закупки.',
    nowWhere: IN_OPS_BOT,
  },
  {
    code: 'study',
    title: 'Обучение',
    path: '/study',
    group: 'Люди',
    ready: SECTION_READY.study,
    about: 'Справочник блюд, курсы и тесты, Наставник, редактор и отчёты.',
    nowWhere: { text: 'в приложении обучающего бота' },
  },
  {
    code: 'team',
    title: 'Команда',
    path: '/team',
    group: 'Люди',
    ready: SECTION_READY.team,
    about: 'Сотрудники, должности и права, заявки на доступ.',
    nowWhere: IN_OPS_BOT,
  },
  {
    code: 'accountant',
    title: 'Бухгалтер',
    path: '/accountant',
    group: 'Люди',
    ready: SECTION_READY.accountant,
    about: 'Бухгалтер-калькулятор: спросите о приходах, долгах, ценах и себестоимости.',
  },
  {
    code: 'settings',
    title: 'Настройки',
    path: '/settings',
    group: 'Система',
    ready: SECTION_READY.settings,
    about: 'Мои уведомления, настройки агентов, ресторан, данные и сбор.',
    nowWhere: IN_OPS_BOT,
  },
];

export const GROUPS = ['Работа', 'Цифры', 'Люди', 'Система'] as const;

function hasAny(rights: Record<string, string> | undefined, code: string): boolean {
  return Boolean(rights && rights[code]);
}

/** Может ли человек видеть раздел — таблица §2 ТЗ портала. */
export function canSee(code: IconName, nav: Navigation): boolean {
  const { ops, finance, study } = nav.services;
  const host = nav.user.is_host;
  switch (code) {
    case 'today':
    case 'settings':
      return true;
    case 'receipts':
      return host || ops?.rights?.receipts === 'edit';
    case 'payments':
      // Вкладки «Поставщики» и «Выписка банка» открывает право «Сверка с
      // поставщиками и банком» (D261), остальные — право «Оплаты»: раздел
      // виден, если открыта хоть одна вкладка (D235, D267).
      return host || hasAny(ops?.rights, 'payments') || hasAny(ops?.rights, 'reconcile');
    case 'shift':
      return host || Boolean(ops);
    case 'finance':
      // Любой открытый раздел финансов (D230): вход в сервис даёт все,
      // владелец снимает лишнее галочками в карточке человека.
      return host || Object.keys(finance?.rights ?? {}).length > 0;
    case 'stock':
      return host || hasAny(ops?.rights, 'stock_audit');
    case 'study':
      return host || Boolean(study);
    case 'team':
      // «Владелец; просмотр — управляющий» (§2 ТЗ портала). Признак даёт
      // сервер: где проходит граница, решает `require_team_view_web`, а не
      // догадка портала по уровню доступа.
      return host || nav.user.can_view_team;
    case 'accountant':
      // Галочка «Бухгалтер-калькулятор» в правах операционного агента
      // (D236): вход у бухгалтера — по правам `ops`.
      return host || hasAny(ops?.rights, 'accountant');
  }
}

export function visibleSections(nav: Navigation): SectionDef[] {
  return SECTIONS.filter((section) => canSee(section.code, nav));
}

/** Какой раздел открыт по адресу. */
export function sectionForPath(path: string): SectionDef | undefined {
  if (path === '/' || path === '') return SECTIONS[0];
  return SECTIONS.find((section) => section.path !== '/' && path.startsWith(section.path));
}
